"use client"

import { useState } from "react"

export function NewsletterSignup({ variant = "footer" }: { variant?: "footer" | "page" }) {
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle")
  const [message, setMessage] = useState("")

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email) return

    setStatus("loading")
    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name: name || undefined }),
      })
      const data = await res.json()
      if (res.ok) {
        setStatus("success")
        setMessage("You're subscribed! We'll be in touch.")
        setEmail("")
        setName("")
      } else {
        setStatus("error")
        setMessage(data.error || "Something went wrong")
      }
    } catch {
      setStatus("error")
      setMessage("Something went wrong. Please try again.")
    }
  }

  if (variant === "footer") {
    return (
      <div>
        <h4 className="overline mb-4">Newsletter</h4>
        {status === "success" ? (
          <p className="text-[13px] text-[var(--muted-foreground)]">{message}</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-2">
            <p className="text-[13px] text-[var(--muted-foreground)] leading-relaxed max-w-[260px] mb-3">
              Open calls, articles, and top residencies — delivered monthly.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full max-w-[260px] px-3 py-2 text-[13px] bg-[var(--background)] border border-[var(--border)] rounded-md focus:outline-none focus:ring-1 focus:ring-[var(--foreground)] transition-colors"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="block px-4 py-2 text-[13px] font-medium bg-[var(--foreground)] text-[var(--background)] rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {status === "loading" ? "Subscribing..." : "Subscribe"}
            </button>
            {status === "error" && (
              <p className="text-[12px] text-red-600">{message}</p>
            )}
          </form>
        )}
      </div>
    )
  }

  // Page variant — larger, centered
  return (
    <div className="max-w-md mx-auto">
      {status === "success" ? (
        <div className="text-center py-8">
          <h3 className="font-editorial text-2xl mb-2">You're in!</h3>
          <p className="text-[var(--muted-foreground)]">{message}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="newsletter-name" className="block text-sm font-medium mb-1">
              Name <span className="text-[var(--muted-foreground)]">(optional)</span>
            </label>
            <input
              id="newsletter-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full px-4 py-3 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--foreground)] transition-colors"
            />
          </div>
          <div>
            <label htmlFor="newsletter-email" className="block text-sm font-medium mb-1">
              Email
            </label>
            <input
              id="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full px-4 py-3 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--foreground)] transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={status === "loading"}
            className="w-full px-6 py-3 text-sm font-semibold bg-[var(--foreground)] text-[var(--background)] rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {status === "loading" ? "Subscribing..." : "Subscribe to the Newsletter"}
          </button>
          {status === "error" && (
            <p className="text-sm text-red-600 text-center">{message}</p>
          )}
          <p className="text-xs text-[var(--muted-foreground)] text-center">
            No spam, ever. Unsubscribe anytime with one click.
          </p>
        </form>
      )}
    </div>
  )
}
